// Piutang — global view of all outstanding (tempo) transactions across
// every event. The cashier/owner can:
//   - Scan the full list with status / due-date / customer info
//   - Filter by status (Semua / Tempo / Overdue) and search by name/phone/invoice
//   - Click through to the customer detail to record a payment, or use
//     the inline "Catat bayar" button to do it from this page
//   - Tap the WA icon to send a pre-filled reminder via wa.me
//
// Owner-and-cashier accessible — piutang is financial context. The
// per-customer view at /customers/:id remains the primary surface for
// cashier-driven collection; this page is the rollup.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet,
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  MessageCircle,
  Search,
  Receipt,
  Banknote,
  QrCode,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { FitText } from '@/components/ui/FitText';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { SearchInput } from '@/components/ui/SearchInput';
import { useAuth } from '@/hooks/useAuth';
import { useEvents } from '@/hooks/useEvents';
import {
  useAllOutstanding,
  useRecordPayment,
} from '@/hooks/useReceivables';
import { derivePaymentStatus } from '@/types/domain';
import { Select } from '@/components/ui/Select';
import { cn, formatIDR } from '@/lib/format';
import type { AppTransaction, PaymentMethod } from '@/types/domain';

type StatusFilter = 'all' | 'tempo' | 'overdue';

export default function Piutang() {
  const { data: outstanding, isLoading } = useAllOutstanding();
  const { data: events } = useEvents();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [eventFilter, setEventFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [payingTx, setPayingTx] = useState<AppTransaction | null>(null);

  // Event-name lookup so the table can show "Event X" next to each
  // invoice when the user picks "Semua event" — same disambiguation as
  // the /transactions page.
  const eventNameById = useMemo(() => {
    const m = new Map<string, string>();
    (events ?? []).forEach((e) => m.set(e.id, e.name));
    return m;
  }, [events]);

  const stats = useMemo(() => {
    if (!outstanding) {
      return {
        total: 0,
        overdueCount: 0,
        tempoCount: 0,
        customerCount: 0,
        soonestDue: null as number | null,
      };
    }
    let total = 0;
    let overdueCount = 0;
    let tempoCount = 0;
    const customerSet = new Set<string>();
    let soonestDue: number | null = null;
    for (const tx of outstanding) {
      if (eventFilter && tx.eventId !== eventFilter) continue;
      total += tx.outstandingAmount;
      tempoCount += 1;
      const status = derivePaymentStatus(tx);
      if (status === 'overdue') overdueCount += 1;
      if (tx.customerId) customerSet.add(tx.customerId);
      if (tx.dueDate !== undefined) {
        if (soonestDue === null || tx.dueDate < soonestDue) {
          soonestDue = tx.dueDate;
        }
      }
    }
    return {
      total,
      overdueCount,
      tempoCount,
      customerCount: customerSet.size,
      soonestDue,
    };
  }, [outstanding, eventFilter]);

  const filtered = useMemo(() => {
    if (!outstanding) return [];
    const q = search.trim().toLowerCase();
    return outstanding
      .filter((tx) => {
        if (eventFilter && tx.eventId !== eventFilter) return false;
        if (statusFilter === 'overdue') {
          if (derivePaymentStatus(tx) !== 'overdue') return false;
        } else if (statusFilter === 'tempo') {
          if (derivePaymentStatus(tx) !== 'pending_payment') return false;
        }
        if (!q) return true;
        const name = (tx.customerSnapshot?.name ?? '').toLowerCase();
        const phone = (tx.customerSnapshot?.phone ?? '').toLowerCase();
        const invoice = tx.invoiceNumber.toLowerCase();
        if (name.includes(q)) return true;
        if (phone.includes(q)) return true;
        if (invoice.includes(q)) return true;
        return false;
      })
      .sort((a, b) => {
        const aStatus = derivePaymentStatus(a);
        const bStatus = derivePaymentStatus(b);
        if (aStatus === 'overdue' && bStatus !== 'overdue') return -1;
        if (bStatus === 'overdue' && aStatus !== 'overdue') return 1;
        const aDue = a.dueDate ?? Number.MAX_SAFE_INTEGER;
        const bDue = b.dueDate ?? Number.MAX_SAFE_INTEGER;
        return aDue - bDue;
      });
  }, [outstanding, statusFilter, eventFilter, search]);

  return (
    <>
      <PageHeader
        overline="Receivables"
        title="Piutang Aktif"
        description="Daftar semua transaksi tempo yang belum lunas — lintas event."
      />

      {/* ── Stats strip ── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-5">
        <StatTile
          icon={<Wallet size={16} />}
          label="Total piutang"
          value={isLoading ? '…' : formatIDR(stats.total)}
          sub={`${stats.tempoCount} invoice`}
          tone="primary"
        />
        <StatTile
          icon={<AlertTriangle size={16} />}
          label="Overdue"
          value={isLoading ? '…' : String(stats.overdueCount)}
          sub={stats.overdueCount === 0 ? 'Aman' : 'Perlu ditagih'}
          tone={stats.overdueCount > 0 ? 'danger' : 'success'}
        />
        <StatTile
          icon={<CalendarClock size={16} />}
          label="Soonest jatuh tempo"
          value={
            isLoading
              ? '…'
              : stats.soonestDue
                ? format(stats.soonestDue, 'd MMM', { locale: idLocale })
                : '—'
          }
          sub={stats.soonestDue ? formatRelativeSoon(stats.soonestDue) : 'Tidak ada'}
          tone="warning"
        />
        <StatTile
          icon={<Receipt size={16} />}
          label="Customer berutang"
          value={isLoading ? '…' : String(stats.customerCount)}
          sub="Unique"
          tone="info"
        />
      </div>

      {/* ── Filters ── */}
      <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="inline-flex rounded border border-line bg-surface p-0.5">
          {(
            [
              { id: 'all', label: 'Semua' },
              { id: 'tempo', label: 'Tempo' },
              { id: 'overdue', label: 'Overdue' },
            ] as { id: StatusFilter; label: string }[]
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setStatusFilter(opt.id)}
              className={cn(
                'px-3 py-1.5 text-caption font-medium rounded transition-colors',
                statusFilter === opt.id
                  ? 'bg-primary text-white'
                  : 'text-ink-subtle hover:text-ink',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="w-full sm:w-56">
            <Select
              value={eventFilter}
              onChange={(v) => setEventFilter(v)}
              aria-label="Filter event"
            >
              <option value="">Semua event</option>
              {(events ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} ({e.code}) {e.status === 'closed' ? '· closed' : ''}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-72">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Cari customer / invoice / phone…"
              aria-label="Cari piutang"
            />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      {isLoading ? (
        <LoadingState message="Memuat piutang…" />
      ) : !outstanding || outstanding.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CheckCircle2 size={24} strokeWidth={1.5} />}
            title="Tidak ada piutang aktif"
            description="Semua transaksi tempo sudah lunas. 🎉"
          />
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Search size={24} strokeWidth={1.5} />}
            title="Tidak ada hasil"
            description="Coba ubah filter atau kata kunci pencarian."
          />
        </Card>
      ) : (
        <Card padding="none">
          <Table>
            <THead>
              <TR>
                <TH>Invoice</TH>
                <TH>Customer</TH>
                <TH>Event</TH>
                <TH>Tanggal</TH>
                <TH>Jatuh tempo</TH>
                <TH>Status</TH>
                <TH className="text-right">Sisa piutang</TH>
                <TH className="text-right">Aksi</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((tx) => (
                <PiutangRow
                  key={tx.id}
                  tx={tx}
                  eventName={eventNameById.get(tx.eventId)}
                  onPayClick={() => setPayingTx(tx)}
                />
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {payingTx && (
        <PayModal tx={payingTx} onClose={() => setPayingTx(null)} />
      )}
    </>
  );
}

// ==== Row ====

function PiutangRow({
  tx,
  eventName,
  onPayClick,
}: {
  tx: AppTransaction;
  eventName?: string;
  onPayClick: () => void;
}) {
  const status = derivePaymentStatus(tx);
  const isOverdue = status === 'overdue';
  const dueDate = tx.dueDate;
  const phone = tx.customerSnapshot?.phone;

  const waLink = useMemo(() => {
    if (!phone) return null;
    const normalized = phone.replace(/[^0-9]/g, '');
    if (!normalized) return null;
    const intl = normalized.startsWith('0')
      ? `62${normalized.slice(1)}`
      : normalized.startsWith('62')
        ? normalized
        : `62${normalized}`;
    const dueText = dueDate
      ? format(dueDate, 'd MMM yyyy', { locale: idLocale })
      : '—';
    const msg =
      `Halo ${tx.customerSnapshot?.name ?? 'Bapak/Ibu'},\n\n` +
      `Ini pengingat untuk invoice ${tx.invoiceNumber} ` +
      `senilai ${formatIDR(tx.outstandingAmount)} yang jatuh tempo ${dueText}. ` +
      `Mohon konfirmasi kalau sudah dibayar ya. Terima kasih! 🙏`;
    return `https://wa.me/${intl}?text=${encodeURIComponent(msg)}`;
  }, [phone, tx.invoiceNumber, tx.outstandingAmount, tx.customerSnapshot?.name, dueDate]);

  return (
    <TR>
      <TD>
        <Link
          to={`/invoice/${tx.id}`}
          className="font-mono text-caption text-primary hover:underline"
        >
          {tx.invoiceNumber}
        </Link>
      </TD>
      <TD>
        {tx.customerId ? (
          <Link
            to={`/customers/${tx.customerId}`}
            className="text-body text-ink hover:text-primary"
          >
            {tx.customerSnapshot?.name ?? '—'}
          </Link>
        ) : (
          <span className="text-ink-subtle italic">Walk-in</span>
        )}
        {tx.customerSnapshot?.phone && (
          <p className="text-caption text-ink-subtle">
            {tx.customerSnapshot.phone}
          </p>
        )}
      </TD>
      <TD>
        <span className="text-caption text-ink truncate inline-block max-w-[140px]">
          {eventName ?? '—'}
        </span>
      </TD>
      <TD>
        <span className="text-caption text-ink-subtle whitespace-nowrap">
          {tx.createdAt
            ? format(tx.createdAt, 'd MMM yyyy', { locale: idLocale })
            : '—'}
        </span>
      </TD>
      <TD>
        {dueDate ? (
          <span
            className={cn(
              'text-caption whitespace-nowrap',
              isOverdue ? 'text-danger font-medium' : 'text-ink',
            )}
          >
            {format(dueDate, 'd MMM yyyy', { locale: idLocale })}
          </span>
        ) : (
          <span className="text-caption text-ink-subtle">—</span>
        )}
      </TD>
      <TD>
        {isOverdue ? (
          <Chip variant="danger">
            <AlertTriangle size={11} className="mr-0.5" /> Overdue
          </Chip>
        ) : (
          <Chip variant="info">Tempo</Chip>
        )}
      </TD>
      <TD className="text-right">
        <span
          className={cn(
            'font-semibold tabular-nums',
            isOverdue ? 'text-danger' : 'text-ink',
          )}
        >
          {formatIDR(tx.outstandingAmount)}
        </span>
      </TD>
      <TD className="text-right">
        <div className="inline-flex items-center gap-1">
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Kirim reminder via WhatsApp"
              title="Kirim reminder"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-line text-ink-subtle hover:text-success hover:border-success/30 transition-colors"
            >
              <MessageCircle size={14} />
            </a>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={onPayClick}
            disabled={!tx.customerId}
            title={
              tx.customerId
                ? 'Catat pembayaran'
                : 'Walk-in tidak bisa dibayar dari sini'
            }
          >
            Catat bayar
          </Button>
        </div>
      </TD>
    </TR>
  );
}

// ==== Stat tile ====

function StatTile({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}) {
  const toneClass = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    danger: 'bg-danger/10 text-danger',
    info: 'bg-info/10 text-info',
  }[tone];
  return (
    <div className="p-3 rounded-lg bg-canvas border border-line flex items-start gap-3">
      <span
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          toneClass,
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-overline uppercase text-ink-subtle font-medium tracking-wider">
          {label}
        </p>
        {/* Not `truncate`: this tile holds "Rp 222.500.000" next to "6", and
            ellipsising the total piutang hides the number the page is for. */}
        <FitText className="text-metric font-display font-semibold text-ink tabular-nums">
          {value}
        </FitText>
        <p className="text-caption text-ink-subtle mt-0.5 truncate">{sub}</p>
      </div>
    </div>
  );
}

// ==== Pay modal ====

function PayModal({
  tx,
  onClose,
}: {
  tx: AppTransaction;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [amountInput, setAmountInput] = useState<string>(
    tx.outstandingAmount.toString(),
  );
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const recordPayment = useRecordPayment();

  const amount = useMemo(
    () => Number(amountInput.replace(/[^0-9]/g, '')) || 0,
    [amountInput],
  );

  // Reset internal state every time the modal opens on a new tx.
  useEffect(() => {
    setAmountInput(tx.outstandingAmount.toString());
    setMethod('cash');
    setNotes('');
    setError(null);
  }, [tx.id, tx.outstandingAmount]);

  const submit = async () => {
    setError(null);
    if (!user) return;
    if (amount <= 0) {
      setError('Nominal harus > 0');
      return;
    }
    if (amount > tx.outstandingAmount) {
      setError(
        `Nominal melebihi sisa piutang (${formatIDR(tx.outstandingAmount)})`,
      );
      return;
    }
    try {
      await recordPayment.mutateAsync({
        transactionId: tx.id,
        amount,
        paymentMethod: method,
        notes: notes.trim() || undefined,
        receivedBy: user.uid,
        receivedByName: user.displayName,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal catat pembayaran');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Catat Pembayaran Piutang"
      description={`${tx.invoiceNumber} · Sisa ${formatIDR(tx.outstandingAmount)}`}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <p className="text-small font-medium text-ink mb-2">Metode bayar</p>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                { v: 'cash' as const, l: 'Cash', I: Banknote },
                { v: 'qris' as const, l: 'QRIS', I: QrCode },
                { v: 'transfer' as const, l: 'Transfer', I: Building2 },
              ]
            ).map(({ v, l, I }) => (
              <button
                key={v}
                type="button"
                onClick={() => setMethod(v)}
                className={cn(
                  'flex flex-col items-center gap-1 p-2.5 rounded border text-center',
                  method === v
                    ? 'border-primary bg-primary/5'
                    : 'border-line bg-surface hover:border-primary/30',
                )}
              >
                <I size={14} />
                <span className="text-caption font-medium">{l}</span>
              </button>
            ))}
          </div>
        </div>
        <Input
          label="Nominal dibayar"
          type="text"
          inputMode="numeric"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          error={error ?? undefined}
          leftIcon={<span className="text-caption">Rp</span>}
        />
        <Input
          label="Catatan (opsional)"
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Misal: bayar via BCA, cash di lokasi, dll"
        />
        <div className="p-3 rounded border border-line bg-canvas flex items-center justify-between text-caption">
          <span className="text-ink-subtle">Sisa setelah bayar</span>
          <span className="font-semibold text-ink tabular-nums">
            {formatIDR(Math.max(0, tx.outstandingAmount - amount))}
          </span>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          loading={recordPayment.isPending}
          disabled={amount <= 0 || amount > tx.outstandingAmount}
        >
          Catat pembayaran
        </Button>
      </div>
    </Modal>
  );
}

// Tiny helper to format "soonest due" as relative to today.
function formatRelativeSoon(ms: number): string {
  const diffDays = Math.round((ms - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `${Math.abs(diffDays)} hari lalu`;
  if (diffDays === 0) return 'Hari ini';
  if (diffDays === 1) return 'Besok';
  return `${diffDays} hari lagi`;
}
